import React, { useState } from "react";
import watchwicketLogo from "../../assets/file_00000000711072438e9d72dabae9eda2.png";
import AuthModal from "../AuthModal";
import { navigate } from "../../utils/router";

interface PublicLayoutProps {
  children: React.ReactNode;
  activePath?: string;
  showBottomNav?: boolean;
}

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/live", label: "Live" },
  { path: "/fixtures", label: "Fixtures" },
  { path: "/results", label: "Results" },
  { path: "/leagues", label: "Leagues" },
];

export default function PublicLayout({ children, activePath, showBottomNav = false }: PublicLayoutProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen relative overflow-x-hidden">
      <div className="ph-bg-base" />
      <div className="ph-bg-lights" />
      <div className="ph-bg-texture" />
      <div className="ph-bg-vignette" />

      <header className="sticky top-0 z-50 ph-header border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-slate-300 hover:text-white p-2 -ml-2 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            <div className="flex items-center gap-2.5 lg:flex-1">
              <button onClick={() => navigate("/")} className="flex items-center gap-2.5 group">
                <img src={watchwicketLogo} alt="WatchWicket" className="w-9 h-9 sm:w-10 sm:h-10 object-contain drop-shadow-lg" />
                <div className="flex flex-col">
                  <span className="text-lg sm:text-xl font-black tracking-wide leading-none">
                    <span className="text-white">Watch</span>
                    <span className="text-[#00C980]">Wicket</span>
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-[#708A80] tracking-widest uppercase leading-none mt-0.5 hidden sm:block">
                    Live Cricket Scores
                  </span>
                </div>
              </button>
            </div>

            <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
              {navLinks.map((link) => {
                const isActive = activePath === link.path;
                return (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all relative ${
                      isActive
                        ? "text-white bg-white/[0.06]"
                        : "text-[#9FB6AC] hover:text-white hover:bg-white/[0.03]"
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-[#00C980] rounded-full" />
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center justify-end lg:flex-1">
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 ph-btn-login px-4 py-2 rounded-lg font-semibold text-sm transition-all text-white"
                aria-label="Login to ScoreBox"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="hidden sm:inline">Login</span>
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/[0.06] ph-mobile-menu">
            <nav className="flex flex-col p-3 space-y-0.5">
              {navLinks.map((link) => {
                const isActive = activePath === link.path;
                return (
                  <button
                    key={link.path}
                    onClick={() => { setMobileMenuOpen(false); navigate(link.path); }}
                    className={`text-left px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
                      isActive
                        ? "bg-[#00C980]/10 text-white border border-[#00C980]/20"
                        : "text-[#9FB6AC] hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {link.label}
                  </button>
                );
              })}
              <div className="pt-2">
                <button
                  onClick={() => { setMobileMenuOpen(false); setShowAuthModal(true); }}
                  className="w-full ph-btn-login text-white px-4 py-3 rounded-xl font-semibold text-sm text-center"
                >
                  Login to ScoreBox
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="relative z-10" style={{ paddingBottom: showBottomNav ? "calc(76px + env(safe-area-inset-bottom, 0px))" : "3rem" }}>
        {children}
      </main>

      {!showBottomNav && (
        <footer className="relative z-10 border-t border-white/[0.04] py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-[#708A80] text-sm">
              &copy; {new Date().getFullYear()} WatchWicket. Live cricket scores for local and community cricket.
            </p>
          </div>
        </footer>
      )}

      {showBottomNav && <BottomNavPublic activePath={activePath} />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      <style>{`
        .ph-bg-base {
          position: fixed; inset: 0; z-index: 0;
          background: linear-gradient(175deg, #041F17 0%, #031B13 35%, #062A1E 65%, #031B13 100%);
        }
        .ph-bg-lights {
          position: fixed; inset: 0; z-index: 1; pointer-events: none;
          background:
            radial-gradient(ellipse 900px 500px at 15% 10%, rgba(0,201,128,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 700px 400px at 85% 25%, rgba(0,201,128,0.05) 0%, transparent 60%),
            radial-gradient(ellipse 600px 350px at 50% 75%, rgba(0,201,128,0.04) 0%, transparent 60%),
            radial-gradient(ellipse 400px 250px at 70% 60%, rgba(214,184,90,0.02) 0%, transparent 60%);
        }
        .ph-bg-texture {
          position: fixed; inset: 0; z-index: 2; pointer-events: none; opacity: 0.15;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
        }
        .ph-bg-vignette {
          position: fixed; inset: 0; z-index: 3; pointer-events: none;
          background: radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.45) 100%);
        }
        .ph-header {
          background: rgba(3, 27, 19, 0.92);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .ph-mobile-menu {
          background: rgba(3, 27, 19, 0.96);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
        }
        .ph-btn-login {
          background: linear-gradient(135deg, #00C980 0%, #00A86B 100%);
          box-shadow: 0 2px 8px rgba(0,201,128,0.2), inset 0 1px 0 rgba(255,255,255,0.1);
        }
        .ph-btn-login:hover {
          background: linear-gradient(135deg, #19E69B 0%, #00C980 100%);
        }
      `}</style>
    </div>
  );
}

const bottomNavItems = [
  {
    path: "/",
    label: "Home",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill={active ? "#00C980" : "#708A80"} viewBox="0 0 20 20">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
    ),
  },
  {
    path: "/leagues",
    label: "Leagues",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill="none" stroke={active ? "#00C980" : "#708A80"} viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-4.5A3.375 3.375 0 0019.875 10.875V8.25a3.375 3.375 0 00-3.375-3.375h-9A3.375 3.375 0 004.125 8.25v2.625A3.375 3.375 0 007.5 14.25v4.5" />
      </svg>
    ),
  },
  {
    path: "/live",
    label: "Live",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke={active ? "#00C980" : "#708A80"} strokeWidth={2}>
        <circle cx="12" cy="12" r="3" fill={active ? "#00C980" : "#708A80"} />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M5.636 18.879a9 9 0 0112.728 0" transform="rotate(-90 12 12)" />
      </svg>
    ),
  },
  {
    path: "/fixtures",
    label: "Fixtures",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke={active ? "#00C980" : "#708A80"} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    path: "/results",
    label: "Results",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke={active ? "#00C980" : "#708A80"} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    path: "/more",
    label: "More",
    icon: (active: boolean) => (
      <svg className="w-[22px] h-[22px]" fill={active ? "#00C980" : "#708A80"} viewBox="0 0 20 20">
        <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

function BottomNavPublic({ activePath }: { activePath?: string }) {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Main navigation"
    >
      <div
        className="border-t border-white/[0.06]"
        style={{
          background: "rgba(3, 20, 14, 0.94)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        <div className="max-w-lg mx-auto grid grid-cols-6 px-1">
          {bottomNavItems.map((item) => {
            const isActive = activePath === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center py-2 gap-0.5 transition-colors relative"
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
              >
                {isActive && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-[#00C980] rounded-full" />
                )}
                <div className={`p-1 rounded-lg transition-colors ${isActive ? "bg-[#00C980]/10" : ""}`}>
                  {item.icon(isActive)}
                </div>
                <span
                  className="text-[10px] font-semibold"
                  style={{ color: isActive ? "#00C980" : "#708A80" }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
